// Registry of hosted model spaces — one playground + SEO page per gateway media
// model (fal-routed image/video/3d/audio, in the style of fal.ai's /models pages).
// Adding an entry here creates /spaces/<slug>, its <title>/meta, and its
// sitemap/prerender coverage. Keep slugs in sync with sitemapSpaceSlugs in
// server/sitemap.go.

export type SpaceTask =
  | 'text-to-image'
  | 'image-edit'
  | 'image-to-3d'
  | 'model-to-3d'
  | 'text-to-video'
  | 'image-to-video'
  | 'reference-to-video'
  | 'video-relight'
  | 'text-to-audio';

export type SpaceCategory = 'image' | 'video' | '3d' | 'audio';

export interface CogSpaceConfig {
  /** Catalog template used by the zero-config /api/cogs/run bridge. */
  template: string;
  /** Stable name shared by compatible Spaces so they reuse one warm worker. */
  deploymentName: string;
  aspectRatios?: string[];
  steps?: { min: number; max: number; default: number };
  allowSeed?: boolean;
  structuredPrompt?: boolean;
  /** Optional H3 final-frame keyframe, separate from first-frame loop mode. */
  lastFrame?: boolean;
  /** Model-native first-frame-as-last-frame conditioning, not post-processing. */
  loop?: boolean;
  audio?: boolean;
  codecs?: string[];
  encodeQuality?: { min: number; max: number; default: number };
  notice?: string;
}

export interface Space {
  slug: string;
  /** Gateway catalog model id sent as `model` in the request body. */
  model: string;
  name: string;
  task: SpaceTask;
  category: SpaceCategory;
  tagline: string;
  /** Meta description (also the index-card copy). */
  description: string;
  /** SEO body copy under the playground. */
  about: string[];
  price: string;
  defaultPrompt?: string;
  defaultImageUrl?: string;
  defaultModelUrl?: string;
  sizes?: string[];
  /** Seconds choices for video/audio tasks. */
  durations?: number[];
  /** Extra fixed fields merged into the request payload. */
  extraPayload?: Record<string, string | number | boolean>;
  /** Optional direct Cog runtime. Generic Spaces otherwise use the media gateway. */
  cog?: CogSpaceConfig;
  /** Override the default task endpoint (e.g. Music3 uses /v1/music/generations). */
  endpoint?: string;
  /** Optional lyrics field for music models. */
  lyrics?: boolean;
  /** Expose a reproducible seed on non-Cog generation endpoints. */
  allowSeed?: boolean;
  /** Poll attempts for long GPU jobs. Default 180 (~7.5 minutes). */
  pollAttempts?: number;
}

const FLUX_SIZES = ['1024x1024', '1152x768', '768x1152', '1360x768', '768x1360'];
const IMG_PROMPT =
  'A beige ceramic coffee mug on a wooden table, soft natural window light, editorial product photo';
const VIDEO_PROMPT = 'A cinematic fly-through of a neon-lit cyberpunk street at night, rain reflections, slow dolly';
const REF_IMAGE = 'https://openpathsstatic.openpaths.io/static/uploads/image-to-3d/sword-reference.jpg';
const REF_GLB = 'https://openpathsstatic.openpaths.io/static/uploads/image-to-3d/sword-pixal3d.glb';

export const spaces: Space[] = [
  // ---- Image generation ----
  {
    slug: 'flux-schnell',
    model: 'flux-schnell',
    name: 'FLUX.1 Schnell',
    task: 'text-to-image',
    category: 'image',
    tagline: 'FLUX distilled for speed: text-to-image in a few steps.',
    description:
      'FLUX.1 Schnell on the app.nz gateway: text-to-image at $0.003 per image with an OpenAI-compatible API, live playground, and prepaid credits.',
    about: [
      'FLUX.1 Schnell is Black Forest Labs\' distilled FLUX variant tuned for speed. It generates in a handful of diffusion steps, so it fits thumbnails, drafts, high-volume pipelines, and anything where latency matters more than the last few percent of detail.',
      'It runs on the same /v1/images/generations endpoint as every other image model — switch to flux-pro or gpt-image-2 by changing one field. Requests are metered per image against prepaid credits.',
    ],
    price: '$0.003 / image',
    defaultPrompt: IMG_PROMPT,
    sizes: FLUX_SIZES,
  },
  {
    slug: 'flux-dev',
    model: 'flux-dev',
    name: 'FLUX.1 Dev',
    task: 'text-to-image',
    category: 'image',
    tagline: 'Open-weights FLUX for text-to-image, with more detail than Schnell.',
    description:
      'FLUX.1 Dev on app.nz: $0.025 per image, OpenAI-compatible API, playground, and usage-based billing.',
    about: [
      'FLUX.1 Dev is the open-weights guidance-distilled FLUX model. It gives more detail, better typography, and closer prompt adherence than Schnell at $0.025 per image, well below Pro. It fits product shots, concept art, and marketing imagery.',
      'Call it through /v1/images/generations with your app.nz key, or try it here first. Images are metered against prepaid credits at the published price.',
    ],
    price: '$0.025 / image',
    defaultPrompt: IMG_PROMPT,
    sizes: FLUX_SIZES,
  },
  {
    slug: 'flux-pro',
    model: 'flux-pro',
    name: 'FLUX 1.1 Pro',
    task: 'text-to-image',
    category: 'image',
    tagline: 'Top tier of the FLUX family, for hero images and client work.',
    description:
      'FLUX 1.1 Pro on the app.nz gateway: $0.04 per image, OpenAI-compatible API, live browser playground.',
    about: [
      'FLUX 1.1 Pro is the flagship FLUX model, priced at the top of the family. Use it when the image is the product — hero banners, ads, packaging mockups, editorial art.',
      'It shares the /v1/images/generations endpoint with every other model on the gateway, so upgrading a pipeline from Schnell or Dev is a one-line change.',
    ],
    price: '$0.04 / image',
    defaultPrompt: IMG_PROMPT,
    sizes: FLUX_SIZES,
  },
  {
    slug: 'flux-klein',
    model: 'klein',
    name: 'FLUX.2 Klein',
    task: 'text-to-image',
    category: 'image',
    tagline: 'FLUX.2\'s compact 4-billion-parameter model at $0.02 per image.',
    description:
      'FLUX.2 Klein (4B) on app.nz: the FLUX.2 architecture at $0.02 per image through the OpenAI-compatible /v1/images/generations endpoint.',
    about: [
      'Klein is the 4-billion-parameter member of the FLUX.2 family: newer architecture and training than FLUX.1 at a fraction of Pro pricing. It handles complex scenes and in-image text better than similarly priced FLUX.1 variants.',
      'Generate here in the playground or call it with model "klein" from any OpenAI SDK pointed at https://app.nz/v1.',
    ],
    price: '$0.02 / image',
    defaultPrompt: IMG_PROMPT,
    sizes: FLUX_SIZES,
  },
  {
    slug: 'gpt-image-2',
    model: 'fal-gpt-image-2',
    name: 'GPT Image 2',
    task: 'text-to-image',
    category: 'image',
    tagline: 'OpenAI\'s image model, hosted with automatic failover.',
    description:
      'GPT Image 2 on the app.nz gateway: instruction following and in-image text at $0.22 per image, with automatic fallback across hosts.',
    about: [
      'GPT Image 2 handles long multi-part prompts, accurate in-image text, charts, UI mockups, and edits that keep the rest of the scene intact. It costs more per image, so most pipelines pair it with a cheap draft model and reserve it for finals.',
      'app.nz routes it across multiple hosts with automatic failover, and the auto-image router picks it when a prompt needs it.',
    ],
    price: '$0.22 / image',
    defaultPrompt: 'A clean infographic poster that says "SHIP IT FRIDAY" in bold type, minimal Swiss design, off-white background',
  },
  {
    slug: 'hidream-o1',
    model: 'hidream-o1-image-dev',
    name: 'HiDream O1',
    task: 'text-to-image',
    category: 'image',
    tagline: 'Image generation billed per output megapixel.',
    description:
      'HiDream O1 (dev) on app.nz: text-to-image billed per output megapixel (~$0.011/MP) through the OpenAI-compatible gateway.',
    about: [
      'HiDream O1 is an open image model with good photorealism and lighting. On app.nz it bills per output megapixel, so smaller renders cost less — useful for iterating at low resolution before a full-size final.',
      'It also has an edit variant (see the HiDream O1 Edit space) that applies prompt-guided changes to an input image.',
    ],
    price: '~$0.011 / megapixel',
    defaultPrompt: IMG_PROMPT,
  },

  {
    slug: 'fast-sdxl',
    model: 'fast-sdxl',
    name: 'Fast SDXL',
    task: 'text-to-image',
    category: 'image',
    tagline: 'Netwrck\'s open-source stable-diffusion-server: 8-step SDXL at $0.003 per image.',
    description:
      'Fast SDXL (Proteus, 8 steps) from the open-source stable-diffusion-server on app.nz: $0.003 per image via the OpenAI-compatible gateway.',
    about: [
      'Fast SDXL runs netwrck\'s open-source stable-diffusion-server: a Proteus-SDXL pipeline distilled to a handful of steps, priced like SDXL Turbo. It is the cheapest model on the gateway for iterating on compositions, thumbnails, and bulk art before a quality pass with a larger model.',
      'The same server also powers the SDXL Style Transfer space — send {model: "fast-sdxl", prompt} to /v1/images/generations, or alias it as "stable-diffusion-server".',
    ],
    price: '$0.003 / image',
    defaultPrompt: IMG_PROMPT,
    sizes: ['1024x1024', '768x768', '512x512', '1024x768', '768x1024'],
  },

  // ---- Image editing ----
  {
    slug: 'sdxl-style-transfer',
    model: 'sdxl-style-transfer',
    name: 'SDXL Style Transfer',
    task: 'image-edit',
    category: 'image',
    tagline: 'Restyle an image along its lines: canny-guided SDXL at $0.003 per image.',
    description:
      'Style transfer on app.nz: canny edge-guided SDXL redraws your image in a described style for $0.003 per image — image in, restyled image out, via the gateway API.',
    about: [
      'SDXL Style Transfer extracts the line structure of your input image (canny ControlNet) and redraws it in the style you describe — anime, watercolor, blueprint, oil paint — preserving composition while fully changing the look. It runs on netwrck\'s open-source stable-diffusion-server at SDXL Turbo pricing.',
      'Send {model: "sdxl-style-transfer", prompt, image_url, strength} to /v1/images/generations. The example below restyles a photo of a fox into anime — swap in your own image URL or upload one.',
    ],
    price: '$0.003 / image',
    defaultPrompt: 'anime style, vibrant colors, clean lineart',
    defaultImageUrl: 'https://netwrckstatic.netwrck.com/static/uploads/test/appnz-probe-fox.webp',
    extraPayload: { strength: 0.75 },
  },
  {
    slug: 'hidream-o1-edit',
    model: 'fal-ai/hidream-o1-image/edit',
    name: 'HiDream O1 Edit',
    task: 'image-edit',
    category: 'image',
    tagline: 'Prompt-guided edits that keep the rest of the image intact.',
    description:
      'Edit an image with a text instruction using HiDream O1 Edit on app.nz — restyle, replace, retouch at ~$0.011 per output megapixel via the gateway API.',
    about: [
      'HiDream O1 Edit takes an input image plus an instruction ("make it night", "replace the mug with a teapot", "watercolor style") and rewrites only what the prompt asks for, preserving composition and identity.',
      'Send {model, prompt, image_url} to /v1/images/generations — the same endpoint as generation, so edit steps drop into existing pipelines unchanged.',
    ],
    price: '~$0.011 / megapixel',
    defaultPrompt: 'Make it golden hour: warm low sun, long soft shadows, keep everything else unchanged',
    defaultImageUrl: REF_IMAGE,
  },
  {
    slug: 'extend-image',
    model: 'extend-image',
    name: 'Extend Image',
    task: 'image-edit',
    category: 'image',
    tagline: 'Outpaint an image: extend the canvas in any direction.',
    description:
      'Extend an image past its borders with outpainting on app.nz: $0.05 per image through the gateway API, with a live playground.',
    about: [
      'Extend Image grows a picture past its original edges, hallucinating coherent surroundings — turn a portrait crop into a wide banner, or recover headroom lost to a tight crop. A prompt steers what the new regions contain.',
      'For FLUX-quality outpainting see the FLUX.2 Pro Outpaint space; this endpoint is the cheaper general-purpose option.',
    ],
    price: '$0.05 / image',
    defaultPrompt: 'Extend the scene naturally, matching lighting and perspective',
    defaultImageUrl: REF_IMAGE,
  },
  {
    slug: 'flux-2-pro-outpaint',
    model: 'fal-ai/flux-2-pro/outpaint',
    name: 'FLUX.2 Pro Outpaint',
    task: 'image-edit',
    category: 'image',
    tagline: 'Canvas extension with the FLUX.2 Pro model.',
    description:
      'Outpaint with FLUX.2 Pro on app.nz: $0.033 for the first megapixel through the OpenAI-compatible gateway.',
    about: [
      'FLUX.2 Pro Outpaint extends images with the FLUX.2 Pro model, so generated regions match the original in texture, grain, and lighting for print and hero placements.',
      'Billing is per megapixel of output ($0.033 first MP, $0.0165 each additional), so cost scales with how much canvas you add.',
    ],
    price: '$0.033 first MP + $0.0165 / extra MP',
    defaultPrompt: 'Continue the environment seamlessly on all sides',
    defaultImageUrl: REF_IMAGE,
  },
  {
    slug: 'smart-resize',
    model: 'smart-resize',
    name: 'Smart Resize',
    task: 'image-edit',
    category: 'image',
    tagline: 'Retarget any image to a new aspect ratio without stretching.',
    description:
      'Resize an image to a new aspect ratio on app.nz: content is extended or recomposed, not stretched. $0.18 per image.',
    about: [
      'Smart Resize retargets an image to a different aspect ratio: subjects stay undistorted while backgrounds extend or recompose. One product shot becomes a square tile, a story, and a wide banner.',
      'Pass the target size alongside the image URL; the result comes back through the standard images response envelope.',
    ],
    price: '$0.18 / image',
    defaultPrompt: '',
    defaultImageUrl: REF_IMAGE,
    sizes: ['1024x1024', '1280x720', '720x1280', '1360x768'],
  },

  // ---- 3D ----
  {
    slug: 'pixal3d',
    model: 'pixal3d-image-to-3d',
    name: 'Pixal3D',
    task: 'image-to-3d',
    category: '3d',
    tagline: 'One photo in, a textured GLB out.',
    description:
      'Convert an image to a textured 3D model (GLB) with Pixal3D on app.nz: $0.30 per generation via the /v1/3d/generations API, previewed in-browser.',
    about: [
      'Pixal3D reconstructs a single reference image into a textured GLB mesh. At $0.30 per model it is the cheapest image-to-3D route on the gateway, and the fastest — used for previz, prototyping, and bulk asset generation.',
      'Jobs run async: POST returns a job id you poll until the GLB URL is ready. The playground below handles polling and renders the result in an interactive viewer.',
    ],
    price: '$0.30 / model',
    defaultImageUrl: REF_IMAGE,
  },
  {
    slug: 'meshy-v6',
    model: 'meshy-v6-image-to-3d',
    name: 'Meshy v6',
    task: 'image-to-3d',
    category: '3d',
    tagline: 'Image-to-3D with topology and polycount control.',
    description:
      'Meshy v6 image-to-3D on app.nz: textured meshes with topology and polycount control, $0.80 per generation through the gateway API.',
    about: [
      'Meshy v6 trades price for mesh detail: finer topology, better texture projection, and controls for target polycount and remeshing. Use it when the asset ships in a real scene rather than a prototype.',
      'Compare against Pixal3D (fastest) and Tripo P1 (balanced) — all three share the same /v1/3d/generations request shape, so switching engines is a one-field change.',
    ],
    price: '$0.80 / model',
    defaultImageUrl: REF_IMAGE,
  },
  {
    slug: 'tripo-p1',
    model: 'tripo-p1-image-to-3d',
    name: 'Tripo P1',
    task: 'image-to-3d',
    category: '3d',
    tagline: 'Image-to-3D at $0.50 per model, between Pixal3D and Meshy v6.',
    description:
      'Tripo P1 image-to-3D on app.nz: reliable textured GLB reconstruction at $0.50 per generation via the OpenAI-compatible gateway.',
    about: [
      'Tripo P1 sits between Pixal3D and Meshy v6 on the quality/price curve, with consistent silhouettes and textures across product shots, characters, and props.',
      'The async job API and in-browser GLB preview below work identically across all image-to-3D spaces on app.nz.',
    ],
    price: '$0.50 / model',
    defaultImageUrl: REF_IMAGE,
  },
  {
    slug: 'meshy-rigging',
    model: 'meshy-rigging',
    name: 'Meshy Auto-Rig',
    task: 'model-to-3d',
    category: '3d',
    tagline: 'Upload a humanoid mesh, get back a rigged, animatable GLB.',
    description:
      'Automatic character rigging on app.nz: Meshy adds a skeleton and skin weights to a humanoid GLB for $0.20 per rig via /v1/3d/generations.',
    about: [
      'Meshy Auto-Rig takes an unrigged humanoid mesh and returns it with a skeleton and skinning weights, ready for animation in Blender, Unity, or Unreal. It pairs naturally with the image-to-3D spaces: generate a character, then rig it in one more call.',
      'Pass the GLB URL as model_url; the job completes with a download URL for the rigged asset.',
    ],
    price: '$0.20 / rig',
    defaultModelUrl: REF_GLB,
  },
  {
    slug: 'trellis-retexture',
    model: 'trellis-2-retexture',
    name: 'Trellis 2 Retexture',
    task: 'model-to-3d',
    category: '3d',
    tagline: 'Re-skin any mesh from a text prompt — geometry untouched.',
    description:
      'Retexture 3D models with Trellis 2 on app.nz: describe a new material or style and get the same mesh back with fresh textures. $0.24 per run.',
    about: [
      'Trellis 2 Retexture repaints an existing mesh from a prompt — "weathered bronze", "toon-shaded", "sci-fi panels" — without altering geometry or UVs, so one asset yields a whole palette of variants.',
      'Provide model_url plus a prompt; the async job returns a new textured GLB.',
    ],
    price: '$0.24 / run',
    defaultPrompt: 'Weathered bronze with verdigris in the recesses, subtle scratches on edges',
    defaultModelUrl: REF_GLB,
  },

  // ---- Video ----
  {
    slug: 'minimax-h3-text-to-video',
    model: 'minimax-h3',
    name: 'MiniMax H3 Text-to-Video',
    task: 'text-to-video',
    category: 'video',
    tagline: 'Joint video and stereo audio from one prompt on a shared 5090 Cog.',
    description:
      'MiniMax H3 text-to-video on app.nz with native synced audio, flow-matching controls, GPU AV1 delivery, and a shared scale-to-zero Cog worker.',
    about: [
      'MiniMax H3 generates the picture and stereo soundtrack together. This Space exposes the controls the model actually uses: canvas tier, duration, sampling steps, seed, structured prompting, audio, and delivery codec. H3 is guidance-distilled, so there is no CFG, negative-prompt, or alternate-scheduler control. Pick size=audio for a 32×32 near-realtime foley/dialogue sketch (video disposable; keep native audio on).',
      'Text-to-video and image-to-video requests use the same named minimax-h3-shared deployment. A signed-in account therefore pays for one reusable scale-to-zero worker rather than warming two copies of the model. For a naturally closing cinemagraph, use the companion image-to-video Space: loop mode needs a real first-frame anchor.',
    ],
    price: '~$2.24 / GPU-hour, billed per execution second',
    defaultPrompt: 'A quiet moss-covered courtyard after rain, a shallow reflecting pool ripples in a light breeze, slow locked-off cinematic shot; distant birds and soft water ambience',
    sizes: ['balanced', 'preview', 'native', 'audio'],
    durations: [5, 8, 12, 15, 30, 45, 60],
    cog: {
      template: 'minimax-h3',
      deploymentName: 'minimax-h3-shared',
      aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      steps: { min: 8, max: 30, default: 12 },
      allowSeed: true,
      structuredPrompt: true,
      audio: true,
      codecs: ['webm-av1', 'mp4-h264'],
      encodeQuality: { min: 16, max: 45, default: 26 },
      notice: 'Requires the MiniMax H3 license gate (APPNZ_MINIMAX_H3_LICENSE_ACCEPTED=1). Uses the shared scale-to-zero H3 deployment. int8_convrot is the stable default; w4a8 is experimental opt-in. Durations >15s auto-chain ~5s segments with a 1s first_frame overlap (same seed). size=audio forces 32×32 (≤45s, no chain).',
    },
  },
  {
    slug: 'minimax-h3-image-to-video',
    model: 'minimax-h3',
    name: 'MiniMax H3 Image-to-Video',
    task: 'image-to-video',
    category: 'video',
    tagline: 'Animate a first frame, land on a chosen final frame, or generate a natural loop.',
    description:
      'Animate an image with MiniMax H3 on app.nz, including optional model-native first/last loop conditioning, native audio, GPU AV1, and a shared scale-to-zero Cog worker.',
    about: [
      'The input image becomes H3\'s first-frame condition. Add an optional ending keyframe for a first/last-frame transition, or turn on Natural loop to reuse the opening image as the final-frame condition. Loop mode asks the model to create a coherent path back to its start—it is not a reversed clip, cross-fade, or duplicated frame added after generation.',
      'This Space shares the minimax-h3-shared deployment with H3 text-to-video. The fixed sampler is H3\'s flow-match Euler path; useful tuning stays focused on prompt, canvas, duration, steps, seed, audio, and encode quality.',
    ],
    price: '~$2.24 / GPU-hour, billed per execution second',
    defaultPrompt: 'Locked camera. Only the steam, reflections, leaves, and small water ripples move; preserve the composition and return gently to the opening frame; soft courtyard ambience',
    sizes: ['balanced', 'preview', 'native'],
    durations: [5, 8, 12, 15, 30],
    cog: {
      template: 'minimax-h3',
      deploymentName: 'minimax-h3-shared',
      aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      steps: { min: 8, max: 30, default: 12 },
      allowSeed: true,
      structuredPrompt: true,
      lastFrame: true,
      loop: true,
      audio: true,
      codecs: ['webm-av1', 'mp4-h264'],
      encodeQuality: { min: 16, max: 45, default: 26 },
      notice: 'Requires the MiniMax H3 license gate (APPNZ_MINIMAX_H3_LICENSE_ACCEPTED=1). Uses the shared scale-to-zero H3 deployment. int8_convrot is the stable default; w4a8 is experimental opt-in. Durations >15s auto-chain from the opening frame (loop/last_frame disabled while chaining).',
    },
  },
  {
    slug: 'seedance-2',
    model: 'seedance-2.0-text-to-video',
    name: 'Seedance 2.0',
    task: 'text-to-video',
    category: 'video',
    tagline: 'ByteDance text-to-video from a prompt, with camera and motion control.',
    description:
      'Generate video from text with Seedance 2.0 on app.nz: ~$0.33 per second through the async /v1/videos/generations API.',
    about: [
      'Seedance 2.0 is ByteDance\'s flagship generation model, with camera language, coherent motion, and prompt-following across styles. Clips are billed per output second, so a 6-second draft costs about $2.',
      'Jobs are asynchronous — submit, poll, download. The playground handles the loop and plays the result inline; the same three calls work from any HTTP client.',
    ],
    price: '~$0.33 / second',
    defaultPrompt: VIDEO_PROMPT,
    durations: [4, 6, 8],
  },
  {
    slug: 'seedance-2-fast',
    model: 'seedance-2.0-fast-text-to-video',
    name: 'Seedance 2.0 Fast',
    task: 'text-to-video',
    category: 'video',
    tagline: 'Seedance 2.0 tuned for drafts and volume.',
    description:
      'Seedance 2.0 Fast text-to-video on app.nz: quicker, cheaper clips (~$0.27/second) for iteration and high-volume pipelines.',
    about: [
      'Seedance 2.0 Fast trades a little fidelity for faster turnaround and a lower per-second rate — the right default while iterating on prompts, then re-render finals on full Seedance 2.0 with the same request body.',
    ],
    price: '~$0.27 / second',
    defaultPrompt: VIDEO_PROMPT,
    durations: [4, 6, 8],
  },
  {
    slug: 'seedance-2-image-to-video',
    model: 'seedance-2.0-image-to-video',
    name: 'Seedance 2.0 Image-to-Video',
    task: 'image-to-video',
    category: 'video',
    tagline: 'Animate a still — your image becomes the first frame.',
    description:
      'Turn any image into video with Seedance 2.0 on app.nz: the input image anchors frame one and a prompt directs the motion. ~$0.33 per second.',
    about: [
      'Image-to-video keeps your exact composition, product, or character: the input image is the first frame and the prompt describes camera and subject motion.',
      'Works with generated images too — create in a FLUX space, animate here, all against one prepaid balance.',
    ],
    price: '~$0.33 / second',
    defaultPrompt: 'Slow push-in, steam rising, shallow depth of field',
    defaultImageUrl: REF_IMAGE,
    durations: [4, 6, 8],
  },
  {
    slug: 'seedance-2-reference-to-video',
    model: 'seedance-2.0-reference-to-video',
    name: 'Seedance 2.0 Reference-to-Video',
    task: 'reference-to-video',
    category: 'video',
    tagline: 'Keep a character or product consistent across every shot.',
    description:
      'Reference-to-video with Seedance 2.0 on app.nz: supply reference images of a subject and generate new scenes that keep it consistent. ~$0.33/second.',
    about: [
      'Reference-to-video solves consistency: the reference images define what a character or product looks like, and the prompt puts it in a new scene — different angle, action, or setting, same subject. Useful for multi-shot narratives and product campaigns.',
      'A fast variant (seedance-2.0-fast-reference-to-video) runs the same requests at a lower rate for drafts.',
    ],
    price: '~$0.33 / second',
    defaultPrompt: 'The subject rotates slowly on a marble pedestal, studio lighting, 360 orbit',
    defaultImageUrl: REF_IMAGE,
    durations: [4, 6, 8],
  },
  {
    slug: 'minimax-h3-max-relight',
    model: 'minimax-h3-max-relight',
    name: 'MiniMax H3 Max Relight',
    task: 'video-relight',
    category: 'video',
    tagline: 'Relight any video with a sphere that shows the lighting you want.',
    description:
      'Relight video with MiniMax H3 Max on app.nz: pick a lighting sphere preset or upload your own, from $0.055 per second at 480P through the gateway API.',
    about: [
      'H3 Max Relight takes a source video and a reference sphere render, a ball shaded by the lighting you want, and rewrites the footage so the subject and scene appear lit that way while motion, identity and composition are preserved. Warm key, cool rim, golden hour, neon, candlelight and moonlight are one click each.',
      'Source clips run up to 15 seconds and are resampled to 24 fps and trimmed to 17n+5 frames, with at least 56 frames (about 2.3 seconds). Billing is per source second, rounded up and clamped to 3 to 15 seconds: $0.055 at 480P, $0.088 at 768P, $0.176 at 1080P and $0.352 at 2K.',
      'Send {model: "minimax-h3-max-relight", video_url, reference_image_url, resolution, aspect_ratio, duration} to /v1/videos/generations. duration is the source length in seconds and is required for billing. The call returns when the relit video is ready.',
    ],
    price: 'from $0.055 / second (480P)',
  },
  {
    slug: 'ltx-2-3',
    model: 'ltx-2.3-image-to-video',
    name: 'LTX 2.3',
    task: 'image-to-video',
    category: 'video',
    tagline: 'Lightricks image-to-video at $0.28 per second.',
    description:
      'LTX 2.3 image-to-video on app.nz: $0.28 per second through the gateway video API.',
    about: [
      'LTX 2.3 is Lightricks\' open video model, one of the fastest ways to animate a still, with smooth motion and stable temporals at $0.28 per second.',
      'Same async API as the Seedance spaces; switching models is a one-field change.',
    ],
    price: '$0.28 / second',
    defaultPrompt: 'Gentle parallax, ambient motion, cinematic grade',
    defaultImageUrl: REF_IMAGE,
    durations: [4, 6, 8],
  },
  {
    slug: 'happy-horse',
    model: 'alibaba/happy-horse/image-to-video',
    name: 'Happy Horse',
    task: 'image-to-video',
    category: 'video',
    tagline: 'Alibaba\'s image-to-video model for character motion.',
    description:
      'Happy Horse image-to-video on app.nz: Alibaba\'s video model for character animation at $0.28 per second via /v1/videos/generations.',
    about: [
      'Happy Horse animates character motion, gesture, and stylized movement from a single still. It sits alongside Seedance (realism) and LTX (speed) in the gateway\'s video lineup.',
    ],
    price: '$0.28 / second',
    defaultPrompt: 'The character waves and smiles, lively animated motion',
    defaultImageUrl: REF_IMAGE,
    durations: [4, 6, 8],
  },

  // ---- Audio ----
  {
    slug: 'sound-effects',
    model: 'elevenlabs-sound-effects',
    name: 'Sound Effects',
    task: 'text-to-audio',
    category: 'audio',
    tagline: 'Describe a sound, get an SFX clip back.',
    description:
      'Generate sound effects from text on app.nz: ElevenLabs SFX v2 at $0.002 per second via the async /v1/audio/generations API.',
    about: [
      'Type "heavy wooden door creaking open in a stone hall" and get a sound effect — foley, UI sounds, ambience, impacts. At $0.002/second, a full game soundscape costs cents.',
      'Clips return as hosted audio URLs through the standard job API, and the same endpoint powers SFX in the app.nz agents and editors.',
    ],
    price: '$0.002 / second',
    defaultPrompt: 'Heavy wooden door creaking open slowly in a large stone hall, natural reverb',
    durations: [5, 10, 15, 22],
  },
  {
    slug: 'stable-audio',
    model: 'stable-audio-25',
    name: 'Stable Audio 2.5',
    task: 'text-to-audio',
    category: 'audio',
    tagline: 'Text-to-audio for loops, beds, and musical textures.',
    description:
      'Stable Audio 2.5 on app.nz: generate music beds, loops, and ambient textures from a prompt at $0.20 per generation.',
    about: [
      'Stable Audio 2.5 generates coherent musical passages and ambient beds — background tracks, loopable game audio, tension stingers — from plain-text descriptions with tempo and instrumentation control.',
      'For full songs with vocals see the /music page; this space is the API-first route for programmatic audio.',
    ],
    price: '$0.20 / generation',
    defaultPrompt: 'Warm lo-fi hip hop loop, dusty drums, mellow Rhodes chords, vinyl crackle, 80 BPM',
    durations: [10, 20, 30, 45],
  },
  {
    slug: 'minimax-music3',
    model: 'minimax-music3',
    name: 'MiniMax Music3',
    task: 'text-to-audio',
    category: 'audio',
    tagline: 'Full songs from a prompt on H100 — lyrics optional, instrumental by default.',
    description:
      'Generate music with MiniMax-Music3 on app.nz: from $1.80 per track ($1.50 + $0.80/minute) on scale-to-zero H100, billed only after a successful WAV.',
    about: [
      'MiniMax-Music3 is a large flow-matching music model. Describe style, tempo, and mood; add optional section-tagged lyrics, or leave lyrics empty for an instrumental. Output is a stereo WAV hosted for download.',
      'It runs on a dedicated H100 (H200 fallback) worker that loads the ~38 GB checkpoint only when a request arrives. Cold starts can take several minutes; later requests on a warm worker are much faster. Charged only on success.',
    ],
    price: 'from $1.80 · $0.80/min',
    defaultPrompt: 'Warm analog synthwave at 105 BPM, hopeful chorus, analog drums, no vocals',
    durations: [30, 60, 90, 180],
    endpoint: '/v1/music/generations',
    lyrics: true,
    allowSeed: true,
    pollAttempts: 720,
  },
];

export const spaceBySlug: Record<string, Space> = Object.fromEntries(spaces.map((s) => [s.slug, s]));

export const spaceCategories: Array<{ id: SpaceCategory; label: string }> = [
  { id: 'image', label: 'Image' },
  { id: 'video', label: 'Video' },
  { id: '3d', label: '3D' },
  { id: 'audio', label: 'Audio' },
];

export function spaceMeta(slug: string): { title: string; description: string } | null {
  const s = spaceBySlug[slug];
  if (!s) return null;
  const taskLabel = TASK_LABELS[s.task];
  return { title: `${s.name} — ${taskLabel} API & playground | app.nz`, description: s.description };
}

export const TASK_LABELS: Record<SpaceTask, string> = {
  'text-to-image': 'Text to Image',
  'image-edit': 'Image Editing',
  'image-to-3d': 'Image to 3D',
  'model-to-3d': '3D to 3D',
  'text-to-video': 'Text to Video',
  'image-to-video': 'Image to Video',
  'reference-to-video': 'Reference to Video',
  'video-relight': 'Video Relighting',
  'text-to-audio': 'Text to Audio',
};

export function spaceEndpoint(spaceOrTask: Space | SpaceTask): string {
  if (typeof spaceOrTask !== 'string' && spaceOrTask.endpoint) return spaceOrTask.endpoint;
  if (typeof spaceOrTask !== 'string' && spaceOrTask.cog) return '/api/cogs/run';
  const task = typeof spaceOrTask === 'string' ? spaceOrTask : spaceOrTask.task;
  switch (task) {
    case 'text-to-image':
    case 'image-edit':
      return '/v1/images/generations';
    case 'image-to-3d':
    case 'model-to-3d':
      return '/v1/3d/generations';
    case 'text-to-video':
    case 'image-to-video':
    case 'reference-to-video':
    case 'video-relight':
      return '/v1/videos/generations';
    case 'text-to-audio':
      return '/v1/audio/generations';
  }
}
